import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Background,
    Controls,
    MiniMap,
    NodeResizer,
    ReactFlow,
    ReactFlowProvider,
    applyNodeChanges,
    useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";
import MachinePipelineCard from "./MachinePipelineCard";
import "./LiveSite.css";

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

// The map is a wall display, so it has to refresh itself; until now it only
// loaded on mount and on a manual resync, which is why it looked frozen.
const POLL_INTERVAL_MS = 60000;

// "stale" is a node running behind the heartbeat cron, "off" is one that has not
// reported at all. Collapsing them hides the difference between a late job and a
// dead machine.
const HEARTBEAT_LABELS = {
    on: { label: "On", colour: "success", dot: "on" },
    stale: { label: "Stale", colour: "warning", dot: "stale" },
    off: { label: "Off", colour: "danger", dot: "off" },
};

const heartbeatLook = (state, fallbackOn) =>
    HEARTBEAT_LABELS[state] ??
    (fallbackOn ? HEARTBEAT_LABELS.on : HEARTBEAT_LABELS.off);

const describeAge = (minutes) => {
    if (minutes === null || minutes === undefined) return "never seen";
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    return `${Math.floor(hours / 24)} d ago`;
};

const HeartbeatBadge = ({ state, on, ageMinutes }) => {
    const look = heartbeatLook(state, Number(on) === 1);
    return (
        <span
            className={`badge bg-${look.colour}`}
            title={`Last seen ${describeAge(ageMinutes)}`}
        >
            {look.label}
            {look.label === "Stale" ? ` · ${describeAge(ageMinutes)}` : ""}
        </span>
    );
};

const toast = (icon, title) =>
    Swal.fire({
        toast: true,
        position: "top-end",
        icon,
        title,
        timer: 2800,
        showConfirmButton: false,
    });

const formatTs = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const nodeSize = (node, fallbackW, fallbackH) => ({
    w: Number(node.measured?.width || node.width || node.style?.width || fallbackW),
    h: Number(node.measured?.height || node.height || node.style?.height || fallbackH),
});

const AreaNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    return (
        <div className="live-site-area-node">
            {data.canEdit ? (
                <NodeResizer minWidth={180} minHeight={140} isVisible={selected} />
            ) : null}
            <input
                className="nodrag nopan nowheel live-site-area-name"
                value={data.name || ""}
                disabled={!data.canEdit}
                onChange={(event) => {
                    const name = event.target.value;
                    setNodes((current) =>
                        current.map((node) =>
                            node.id === id
                                ? { ...node, data: { ...node.data, name, label: name } }
                                : node
                        )
                    );
                }}
            />
        </div>
    );
};

const MachineNode = ({ data, selected }) => {
    const progress = data.placement?.progress || { completed: 0, total: 18, percent: 0 };
    const heartbeatOn = Number(data.placement?.heartbeat_on) === 1;
    const look = heartbeatLook(data.placement?.heartbeat_state, heartbeatOn);
    const energyLook = data.placement?.energy_heartbeat_state
        ? heartbeatLook(data.placement.energy_heartbeat_state, false)
        : null;
    return (
        <div className="live-site-machine-node">
            {data.canEdit ? (
                <NodeResizer minWidth={110} minHeight={48} isVisible={selected} />
            ) : null}
            <span
                className={`live-site-heartbeat-dot ${look.dot}`}
                title={`IO heartbeat ${look.label.toLowerCase()}`}
            />
            {energyLook ? (
                <span
                    className={`live-site-heartbeat-dot energy ${energyLook.dot}`}
                    title={`Energy meter heartbeat ${energyLook.label.toLowerCase()}`}
                />
            ) : null}
            <span className="live-site-machine-progress" title="Commissioning checks completed">
                {progress.completed}/{progress.total} · {progress.percent}%
            </span>
            <strong>
                {data.placement?.machine_name}
                {data.placement?.is_representative ? " ★" : ""}
            </strong>
            <span>{data.placement?.io_node_id || "no IO node"}</span>
        </div>
    );
};

const nodeTypes = { siteArea: AreaNode, siteMachine: MachineNode };

const LiveSiteMapInner = ({ canEdit, canResync, onResynced, syncTick = 0 }) => {
    const { apiClient } = useGlobalAPIQueryContext();

    const [floors, setFloors] = useState([]);
    const [areas, setAreas] = useState([]);
    const [placements, setPlacements] = useState([]);
    const [selectedFloorId, setSelectedFloorId] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [status, setStatus] = useState(null);
    const [unplaced, setUnplaced] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [showConfig, setShowConfig] = useState(false);
    const [configRow, setConfigRow] = useState(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [pickerSearch, setPickerSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [resyncingMachine, setResyncingMachine] = useState(false);
    const [newFloorName, setNewFloorName] = useState("");
    const [layoutRevision, setLayoutRevision] = useState(0);
    const [pollTick, setPollTick] = useState(0);

    const loadLayout = useCallback(async () => {
        const response = await apiClient.get("/api/live-site/layout/");
        const data = unwrap(response) || {};
        const nextFloors = data.floors || data.stages || [];
        setFloors(nextFloors);
        setAreas(data.areas || []);
        setPlacements(data.placements || []);
        setSelectedFloorId((current) => {
            if (current && nextFloors.some((floor) => floor.id === current)) {
                return current;
            }
            return nextFloors[0]?.id ?? null;
        });
        setLayoutRevision((value) => value + 1);
    }, [apiClient]);

    useEffect(() => {
        loadLayout().catch((error) => {
            Swal.fire("Unable to load Live Site", error.message, "error");
        });
    }, [loadLayout]);

    useEffect(() => {
        if (!syncTick) return;
        loadLayout().catch(() => {});
    }, [syncTick, loadLayout]);

    // Only the live fields are merged, and into the nodes rather than into
    // `placements`, because rebuilding placements would snap any geometry the
    // user is part-way through dragging back to its saved position.
    const refreshHeartbeats = useCallback(async () => {
        const response = await apiClient.get("/api/live-site/layout/");
        const data = unwrap(response) || {};
        const byMachine = new Map(
            (data.placements || []).map((row) => [row.machine_id, row])
        );
        setNodes((current) =>
            current.map((node) => {
                if (node.data?.kind !== "machine") return node;
                const fresh = byMachine.get(node.data.placement?.machine_id);
                if (!fresh) return node;
                return {
                    ...node,
                    data: {
                        ...node.data,
                        placement: {
                            ...node.data.placement,
                            heartbeat_on: fresh.heartbeat_on,
                            heartbeat_state: fresh.heartbeat_state,
                            energy_heartbeat_state: fresh.energy_heartbeat_state,
                            progress: fresh.progress,
                        },
                    },
                };
            })
        );
    }, [apiClient]);

    useEffect(() => {
        const timer = setInterval(() => {
            refreshHeartbeats().catch(() => {});
            setPollTick((value) => value + 1);
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [refreshHeartbeats]);

    useEffect(() => {
        const onError = (event) => {
            const text = String(event?.message || event?.error?.message || "");
            if (text.toLowerCase().includes("resizeobserver")) {
                event.stopImmediatePropagation();
                event.preventDefault();
            }
        };
        const onRejection = (event) => {
            const text = String(event?.reason?.message || event?.reason || "");
            if (text.toLowerCase().includes("resizeobserver")) {
                event.preventDefault();
            }
        };
        window.addEventListener("error", onError, true);
        window.addEventListener("unhandledrejection", onRejection);
        return () => {
            window.removeEventListener("error", onError, true);
            window.removeEventListener("unhandledrejection", onRejection);
        };
    }, []);

    const floorAreas = useMemo(
        () => areas.filter((area) => (area.floor_id || area.stage_id) === selectedFloorId),
        [areas, selectedFloorId]
    );
    const floorPlacements = useMemo(
        () => placements.filter((row) => (row.floor_id || row.stage_id) === selectedFloorId),
        [placements, selectedFloorId]
    );

    useEffect(() => {
        const areaById = Object.fromEntries(floorAreas.map((area) => [area.id, area]));
        const nextNodes = [
            ...floorAreas.map((area) => ({
                id: `area-${area.id}`,
                type: "siteArea",
                position: { x: area.x, y: area.y },
                data: { name: area.name, kind: "area", area, canEdit: canEdit && !area.locked },
                draggable: canEdit && !area.locked,
                style: { width: area.width, height: area.height },
                zIndex: 1,
            })),
            ...floorPlacements.map((placement) => {
                const parentArea = placement.area_id ? areaById[placement.area_id] : null;
                const width = placement.width || 170;
                const height = placement.height || 64;
                const absX = placement.x;
                const absY = placement.y;
                return {
                    id: `machine-${placement.id}`,
                    type: "siteMachine",
                    position: parentArea
                        ? { x: absX - parentArea.x, y: absY - parentArea.y }
                        : { x: absX, y: absY },
                    parentId: parentArea ? `area-${parentArea.id}` : undefined,
                    extent: parentArea ? "parent" : undefined,
                    data: { kind: "machine", placement, canEdit },
                    draggable: canEdit,
                    style: { width, height },
                    zIndex: 30,
                };
            }),
        ];
        setNodes(nextNodes);
    }, [floorAreas, floorPlacements, canEdit, layoutRevision, selectedFloorId]);

    const onNodesChange = useCallback((changes) => {
        setNodes((current) => applyNodeChanges(changes, current));
    }, []);

    const selectedPlacement = useMemo(() => {
        if (!selectedNodeId?.startsWith("machine-")) return null;
        const id = Number(selectedNodeId.replace("machine-", ""));
        return placements.find((row) => row.id === id) || null;
    }, [selectedNodeId, placements]);

    const selectedAreaNode = useMemo(() => {
        if (!selectedNodeId?.startsWith("area-")) return null;
        return nodes.find((node) => node.id === selectedNodeId) || null;
    }, [selectedNodeId, nodes]);

    useEffect(() => {
        if (!selectedPlacement) {
            setStatus(null);
            return;
        }
        apiClient
            .get(`/api/live-site/machines/${selectedPlacement.machine_id}/status/`)
            .then((response) => setStatus(unwrap(response)))
            .catch(() => setStatus(null));
    }, [apiClient, selectedPlacement, syncTick, pollTick]);

    const resyncMachine = async () => {
        if (!selectedPlacement?.machine_id || !canResync) return;
        setResyncingMachine(true);
        try {
            const response = await apiClient.post("/api/live-site/elorca-heartbeat/", {
                machine_id: selectedPlacement.machine_id,
            });
            const data = unwrap(response);
            if (data?.status) {
                setStatus(data.status);
                const on = Number(data.status?.io?.heartbeat_on) === 1 ? 1 : 0;
                setPlacements((current) =>
                    current.map((row) =>
                        row.machine_id === selectedPlacement.machine_id
                            ? { ...row, heartbeat_on: on }
                            : row
                    )
                );
            }
            if (onResynced) {
                await onResynced();
            }
            toast("success", "Heartbeat resynced");
        } catch (error) {
            Swal.fire(
                "Resync failed",
                error?.response?.data?.results?.message || error.message,
                "error"
            );
        } finally {
            setResyncingMachine(false);
        }
    };

    const saveLayout = async () => {
        setSaving(true);
        try {
            const byId = new Map(nodes.map((node) => [node.id, node]));
            const absolute = (node) => {
                let x = node.position.x;
                let y = node.position.y;
                let parent = node.parentId ? byId.get(node.parentId) : null;
                while (parent) {
                    x += parent.position.x;
                    y += parent.position.y;
                    parent = parent.parentId ? byId.get(parent.parentId) : null;
                }
                return { x, y };
            };

            const areaUpdates = nodes.flatMap((node) => {
                if (node.data?.kind !== "area") return [];
                const { w, h } = nodeSize(node, node.data.area.width, node.data.area.height);
                return [
                    {
                        id: node.data.area.id,
                        name: node.data.name,
                        x: node.position.x,
                        y: node.position.y,
                        width: w,
                        height: h,
                    },
                ];
            });

            const areaRects = nodes
                .filter((node) => node.data?.kind === "area")
                .map((node) => {
                    const { w, h } = nodeSize(node, node.data.area.width, node.data.area.height);
                    const pos = absolute(node);
                    return { id: node.data.area.id, ...pos, w, h };
                });

            const placementUpdates = nodes.flatMap((node) => {
                if (node.data?.kind !== "machine") return [];
                const { w, h } = nodeSize(node, node.data.placement.width || 170, node.data.placement.height || 64);
                const pos = absolute(node);
                const cx = pos.x + w / 2;
                const cy = pos.y + h / 2;
                const parent = areaRects.find(
                    (area) => cx >= area.x && cy >= area.y && cx <= area.x + area.w && cy <= area.y + area.h
                );
                return [
                    {
                        id: node.data.placement.id,
                        x: pos.x,
                        y: pos.y,
                        width: w,
                        height: h,
                        floor_id: selectedFloorId,
                        area_id: parent?.id || null,
                    },
                ];
            });
            await apiClient.put("/api/live-site/layout/", {
                areas: areaUpdates,
                placements: placementUpdates,
            });
            await loadLayout();
            Swal.fire({ icon: "success", title: "Layout saved", timer: 1400, showConfirmButton: false });
        } catch (error) {
            Swal.fire("Save failed", error.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const addFloor = async () => {
        const name = newFloorName.trim();
        if (!name) return;
        try {
            await apiClient.post("/api/live-site/floors/", { name });
            setNewFloorName("");
            await loadLayout();
        } catch (error) {
            Swal.fire("Could not add floor", error?.response?.data?.results?.message || error.message, "error");
        }
    };

    const addArea = async () => {
        if (!selectedFloorId) return;
        try {
            await apiClient.post("/api/live-site/areas/", { floor_id: selectedFloorId });
            await loadLayout();
        } catch (error) {
            Swal.fire("Could not add area", error?.response?.data?.results?.message || error.message, "error");
        }
    };

    const openPicker = async () => {
        try {
            const response = await apiClient.get("/api/live-site/unplaced-machines/");
            setUnplaced(unwrap(response) || []);
            setShowPicker(true);
        } catch (error) {
            Swal.fire("Could not load machines", error.message, "error");
        }
    };

    const placeMachine = async (machine) => {
        try {
            const parentArea = selectedAreaNode?.data?.area;
            const payload = {
                machine_id: machine.id,
                floor_id: selectedFloorId,
                x: parentArea ? parentArea.x + 24 : 220,
                y: parentArea ? parentArea.y + 48 : 220,
            };
            if (parentArea) payload.area_id = parentArea.id;
            await apiClient.post("/api/live-site/placements/", payload);
            setShowPicker(false);
            await loadLayout();
        } catch (error) {
            Swal.fire("Could not place machine", error?.response?.data?.results?.message || error.message, "error");
        }
    };

    const deleteSelected = async () => {
        if (selectedPlacement) {
            await apiClient.delete(`/api/live-site/placements/${selectedPlacement.id}/`);
            setSelectedNodeId(null);
            await loadLayout();
            return;
        }
        if (selectedAreaNode?.data?.area) {
            await apiClient.delete(`/api/live-site/areas/${selectedAreaNode.data.area.id}/`);
            setSelectedNodeId(null);
            await loadLayout();
        }
    };

    const openConfig = async () => {
        if (!selectedPlacement?.machine_id) return;
        setShowConfig(true);
        setConfigLoading(true);
        setConfigRow(null);
        try {
            const response = await apiClient.get(
                `/api/live-site/configuration/${selectedPlacement.machine_id}/`
            );
            setConfigRow(unwrap(response));
        } catch (error) {
            setShowConfig(false);
            Swal.fire(
                "Could not load configuration",
                error?.response?.data?.results?.message || error.message,
                "error"
            );
        } finally {
            setConfigLoading(false);
        }
    };

    const patchConfig = async (machineId, body) => {
        if (!canEdit) return;
        try {
            const response = await apiClient.patch(`/api/live-site/configuration/${machineId}/`, body);
            const updated = unwrap(response);
            setConfigRow(updated);
            if (updated?.progress) {
                setPlacements((current) =>
                    current.map((row) =>
                        row.machine_id === machineId ? { ...row, progress: updated.progress } : row
                    )
                );
            }
            if (updated?.warning) {
                toast("warning", updated.warning);
            }
        } catch (error) {
            Swal.fire("Update failed", error?.response?.data?.results?.message || error.message, "error");
        }
    };

    const filteredUnplaced = unplaced.filter((row) =>
        [row.machine_name, row.department_name, row.io_node_id, row.energy_node_id]
            .join(" ")
            .toLowerCase()
            .includes(pickerSearch.toLowerCase())
    );

    return (
        <>
            <p className="text-soft mb-2">
                {canEdit
                    ? "Floor → area → machine. Resize areas to fit machines; drag a machine inside an area and save."
                    : "Floor → area → machine. Click a machine for status and View config."}
            </p>

            <div className="live-site-stage-row">
                {floors.map((floor) => (
                    <button
                        key={floor.id}
                        type="button"
                        className={`btn btn-sm ${
                            selectedFloorId === floor.id ? "btn-primary" : "btn-outline-light"
                        }`}
                        onClick={() => setSelectedFloorId(floor.id)}
                    >
                        {floor.name}
                    </button>
                ))}
                {canEdit ? (
                    <div className="live-site-add-stage">
                        <input
                            className="form-control form-control-sm"
                            placeholder="New floor"
                            value={newFloorName}
                            onChange={(event) => setNewFloorName(event.target.value)}
                        />
                        <button type="button" className="btn btn-sm btn-dim btn-primary" onClick={addFloor}>
                            Add floor
                        </button>
                    </div>
                ) : null}
            </div>

            {canEdit ? (
                <div className="live-site-toolbar">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addArea} disabled={!selectedFloorId}>
                        Add area
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={openPicker} disabled={!selectedFloorId}>
                        Place machine
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={deleteSelected} disabled={!selectedNodeId}>
                        Remove selected
                    </button>
                    <button type="button" className="btn btn-sm btn-primary" onClick={saveLayout} disabled={saving}>
                        {saving ? "Saving…" : "Save layout"}
                    </button>
                </div>
            ) : null}

            <div className="live-site-layout">
                <div className="live-site-canvas card">
                    {selectedFloorId ? (
                        <ReactFlow
                            nodes={nodes}
                            edges={[]}
                            nodeTypes={nodeTypes}
                            onNodesChange={canEdit ? onNodesChange : undefined}
                            onNodeClick={(_, node) => setSelectedNodeId(node.id)}
                            fitView
                            snapToGrid
                            snapGrid={[12, 12]}
                            nodesDraggable={canEdit}
                            nodesConnectable={false}
                            elementsSelectable
                        >
                            <Background gap={24} color="#DDE8F5" />
                            <Controls />
                            <MiniMap pannable zoomable />
                        </ReactFlow>
                    ) : (
                        <div className="p-4">Add a floor to start mapping the site.</div>
                    )}
                </div>
                <aside className="live-site-panel card card-inner">
                    <h5>Details</h5>
                    {selectedPlacement && status ? (
                        <div className="live-site-status">
                            <p>
                                <strong>{status.machine_name}</strong>
                                {status.is_representative ? (
                                    <span className="badge bg-info ms-1">Representative</span>
                                ) : null}
                            </p>
                            <div className="live-site-block">
                                <h6>IO node</h6>
                                {status.io?.linked ? (
                                    <>
                                        <p>Node ID: {status.io.node_id}</p>
                                        <p>Production pin: {status.io.production_pin || "—"}</p>
                                        <p>Flavour: {status.io.flavour || "—"}</p>
                                        <p>
                                            Heartbeat:{" "}
                                            <HeartbeatBadge
                                                state={status.io.heartbeat_state}
                                                on={status.io.heartbeat_on}
                                                ageMinutes={status.io.heartbeat_age_minutes}
                                            />{" "}
                                            {formatTs(status.io.heartbeat_at)}
                                        </p>
                                        <p>Latest production: {formatTs(status.io.production_at)}</p>
                                        <p>Produced Qty: {status.io.production_quantity ?? "—"}</p>
                                        <p>Cycles (shots): {status.io.production_cycles ?? "—"}</p>
                                    </>
                                ) : (
                                    <p className="text-soft">Not linked</p>
                                )}
                            </div>
                            <div className="live-site-block">
                                <h6>Energy meter</h6>
                                {status.energy?.linked ? (
                                    <>
                                        <p>Node ID: {status.energy.node_id}</p>
                                        <p>Slave ID: {status.energy.slave_id}</p>
                                        <p>Flavour: {status.energy.flavour || "—"}</p>
                                        <p>
                                            Heartbeat:{" "}
                                            <HeartbeatBadge
                                                state={status.energy.heartbeat_state}
                                                on={status.energy.heartbeat_on}
                                                ageMinutes={status.energy.heartbeat_age_minutes}
                                            />{" "}
                                            {formatTs(status.energy.heartbeat_at)}
                                        </p>
                                        <p>Latest energy data: {formatTs(status.energy.last_data_at)}</p>
                                        <p>
                                            kWh:{" "}
                                            {status.energy.kwh_known ? (
                                                status.energy.kwh_total
                                            ) : (
                                                <span
                                                    className="badge bg-secondary"
                                                    title={
                                                        status.energy.last_data_at
                                                            ? `No reading today. Last reading ${formatTs(
                                                                  status.energy.last_data_at
                                                              )}.`
                                                            : "This meter has never sent a reading"
                                                    }
                                                >
                                                    No data
                                                </span>
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-soft">Not linked</p>
                                )}
                            </div>
                            <p className="text-soft mt-2 mb-1">
                                Last pulled from Elorca: {formatTs(status.heartbeat_pulled_at)}
                            </p>
                            {canResync ? (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary me-1"
                                    onClick={resyncMachine}
                                    disabled={resyncingMachine}
                                >
                                    {resyncingMachine ? "Resyncing…" : "Resync this machine"}
                                </button>
                            ) : null}
                            <button type="button" className="btn btn-sm btn-primary mt-2" onClick={openConfig}>
                                View config
                            </button>
                        </div>
                    ) : selectedAreaNode ? (
                        <div>
                            <label className="form-label">Area name</label>
                            <input
                                className="form-control"
                                value={selectedAreaNode.data?.name || ""}
                                disabled={!canEdit}
                                onChange={(event) => {
                                    const name = event.target.value;
                                    setNodes((current) =>
                                        current.map((node) =>
                                            node.id === selectedAreaNode.id
                                                ? { ...node, data: { ...node.data, name, label: name } }
                                                : node
                                        )
                                    );
                                }}
                            />
                            <p className="text-soft mt-2">Resize the area on the canvas, then save so machines stay nested inside it.</p>
                        </div>
                    ) : (
                        <p className="text-soft">Select an area to rename, or a machine for node status.</p>
                    )}
                </aside>
            </div>

            {showPicker ? (
                <div className="live-site-modal-backdrop" onClick={() => setShowPicker(false)}>
                    <div className="card live-site-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="card-inner">
                            <h5>Place a machine</h5>
                            {selectedAreaNode ? (
                                <p className="text-soft">Will be placed inside {selectedAreaNode.data?.name || "the selected area"}.</p>
                            ) : (
                                <p className="text-soft">Select an area first if this machine should sit inside one.</p>
                            )}
                            <input
                                className="form-control mb-2"
                                placeholder="Search machines"
                                value={pickerSearch}
                                onChange={(event) => setPickerSearch(event.target.value)}
                            />
                            <div className="live-site-machine-list">
                                {filteredUnplaced.map((machine) => (
                                    <button
                                        type="button"
                                        key={machine.id}
                                        className="live-site-machine-row"
                                        onClick={() => placeMachine(machine)}
                                    >
                                        <strong>{machine.machine_name}</strong>
                                        <span>
                                            IO {machine.io_node_id || "—"} · Energy {machine.energy_node_id || "—"}
                                            {machine.energy_slave_id ? `/${machine.energy_slave_id}` : ""}
                                        </span>
                                    </button>
                                ))}
                                {filteredUnplaced.length === 0 ? <p>No unplaced machines.</p> : null}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {showConfig ? (
                <div
                    className="live-site-modal-backdrop"
                    onClick={() => setShowConfig(false)}
                >
                    <div
                        className="card live-site-modal live-site-config-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="card-inner">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Machine configuration</h5>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setShowConfig(false)}
                                >
                                    Close
                                </button>
                            </div>
                            {configLoading ? (
                                <p className="text-soft">Loading configuration…</p>
                            ) : (
                                <MachinePipelineCard
                                    row={configRow}
                                    canEdit={canEdit}
                                    onPatch={patchConfig}
                                />
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

const LiveSiteMap = ({ canEdit = false, canResync = false, onResynced, syncTick = 0 }) => (
    <ReactFlowProvider>
        <LiveSiteMapInner canEdit={canEdit} canResync={canResync} onResynced={onResynced} syncTick={syncTick} />
    </ReactFlowProvider>
);

export default LiveSiteMap;
